import { formatMoney } from '@theme/utilities';
import { QuantityBreaksUpdatedEvent, ThemeEvents } from '@theme/events';

class QuantityBreaks extends HTMLElement {
  constructor() {
    super();

    this.default_quantity_selected = null;
    this.quantity_break_items = null;
  }

  connectedCallback() {
    this.default_quantity_selected = this.dataset.quantitySelectedByDefault || 1;
    this.quantity_break_items = this.querySelectorAll('[data-ref="quantity-break-item"]');

    this.updateDefaultQuantitySelected();

    // On déclenche l'événement de mise à jour des quantités quand le DOM est chargé pour éviter les problèmes de prix si la quantité par défaut n'est pas 1
    document.addEventListener('DOMContentLoaded', () => {
      if (this.getSelectedQuantity().dataset.quantity != 1) {
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      }
    });

    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.addEventListener('click', () => {
        this.selectQuantity(quantity_break);
      });
    });

    document.addEventListener(ThemeEvents.variantUpdated, this.onVariantUpdated.bind(this));
  }

  updateDefaultQuantitySelected() {
    this.resetSelectedQuantity();
    this.quantity_break_items[this.default_quantity_selected - 1].classList.add('quantity-break--selected');
  }

  selectQuantity(quantity_break) {
    this.resetSelectedQuantity();
    quantity_break.classList.add('quantity-break--selected');

    this.dispatchEvent(new QuantityBreaksUpdatedEvent());
  }

  resetSelectedQuantity() {
    this.quantity_break_items.forEach((quantity_break) => {
      quantity_break.classList.remove('quantity-break--selected');
    });
  }

  getSelectedQuantity() {
    return this.querySelector('.quantity-break--selected');
  }

  getPrices() {
    return this.getSelectedQuantity().getPrices();
  }

  getSelectedItems() {
    let items = [];

    if (this.getSelectedQuantity().querySelector('[data-ref="quantity-break-variant-picker"]') != null) {
      this.getSelectedQuantity()
        .querySelectorAll('[data-ref="quantity-break-variant-picker"]')
        .forEach((variant_picker) => {
          items.push({
            id: variant_picker.value,
            quantity: 1,
          });
        });
    } else {
      items.push({
        id: this.getSelectedQuantity().dataset.variantId,
        quantity: parseInt(this.getSelectedQuantity().dataset.quantity),
      });
    }

    return items;
  }

  onVariantUpdated(event) {
    const currentQuantityBreaks = this;

    let newQuantityBreaks = currentQuantityBreaks;
    if (event && event.detail != null) {
      newQuantityBreaks = event.detail.data.html.querySelector('[data-ref="quantity-breaks"]');
    }

    if (!newQuantityBreaks || !currentQuantityBreaks) return;

    // Sauvegarde la quantité actuellement sélectionnée
    const selectedQuantity = this.getSelectedQuantity()?.dataset.quantity;

    if (currentQuantityBreaks !== newQuantityBreaks) {
      currentQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]').forEach((quantity_break, index) => {
        const newQuantityBreak = newQuantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]')[index];
        if (newQuantityBreak) {
          quantity_break.render(newQuantityBreak);
        }
      });

      // Restaure la sélection précédente
      if (selectedQuantity) {
        this.resetSelectedQuantity();

        const itemToReselect = Array.from(this.querySelectorAll('[data-ref="quantity-break-item"]')).find(
          (item) => item.dataset.quantity === selectedQuantity,
        );

        if (itemToReselect) {
          itemToReselect.classList.add('quantity-break--selected');
        }
      }

      this.dispatchEvent(new QuantityBreaksUpdatedEvent());
    }
  }
}

customElements.define('quantity-breaks', QuantityBreaks);

class QuantityBreakItem extends HTMLElement {
  constructor() {
    super();
    this.variant_pickers = null;
    this.price = null;
  }

  connectedCallback() {
    // ✅ CORRECTIF : initialisation des propriétés manquantes
    this.variant_pickers = Array.from(
      this.querySelectorAll('[data-ref="quantity-break-variant-picker"]')
    );
    this.price = this.querySelector('[data-ref="quantity-break-price"]');

    this.variant_pickers.forEach((variant_picker) => {
      variant_picker.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      variant_picker.addEventListener('mousedown', (event) => {
        event.stopPropagation();
      });

      variant_picker.addEventListener('change', (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        this.#updateTotalPrice();
        this.#updateVariantThumbnail(variant_picker); // ✅ CORRECTIF : mise à jour miniature
        this.dispatchEvent(new QuantityBreaksUpdatedEvent());
      });
    });
  }

  // ✅ NOUVEAU : met à jour la miniature à côté du select
  #updateVariantThumbnail(variant_picker) {
    const selectedOption = variant_picker.options[variant_picker.selectedIndex];
    const newSrc = selectedOption.dataset.variantImage;
    const newAlt = selectedOption.dataset.variantImageAlt || '';

    const row = variant_picker.closest('.quantity-break__variant-picker-row');
    if (!row) return;

    const thumbnailContainer = row.querySelector('[data-ref="quantity-break-variant-thumbnail"]');
    if (!thumbnailContainer) return;

    if (newSrc) {
      thumbnailContainer.classList.remove('quantity-break__variant-thumbnail--hidden');
      let img = thumbnailContainer.querySelector('img');
      if (!img) {
        img = document.createElement('img');
        img.width = 40;
        img.height = 40;
        img.loading = 'lazy';
        thumbnailContainer.appendChild(img);
      }
      img.src = newSrc;
      img.alt = newAlt;
    } else {
      thumbnailContainer.classList.add('quantity-break__variant-thumbnail--hidden');
    }
  }

  #updateTotalPrice() {
    let totalPrice = 0;
    let totalCompareAtPrice = 0;

    this.variant_pickers.forEach((variant_picker) => {
      const selectedOption = variant_picker.options[variant_picker.selectedIndex];
      totalPrice += parseInt(selectedOption.dataset.price);
      if (selectedOption.dataset.compareAtPrice) {
        totalCompareAtPrice += parseInt(selectedOption.dataset.compareAtPrice);
      } else {
        totalCompareAtPrice += parseInt(selectedOption.dataset.price);
      }
    });

    // DCA 31.03.2026 - Applique la réduction définie sur le quantity-break-item
    const discountType = this.dataset.discountType;
    const quantity = parseInt(this.dataset.quantity);

    if (discountType === 'percentage') {
      const discountPercentage = parseFloat(this.dataset.discountPercentage) / 100;
      const discountPerUnit = Math.floor(totalPrice / quantity * discountPercentage);
      totalPrice = totalPrice - (discountPerUnit * quantity);
    } else if (discountType === 'value') {
      const discountValue = parseFloat(this.dataset.discountValue);
      totalPrice = Math.max(totalPrice - discountValue, 0);
    }

    this.price.querySelector('[data-ref="price"]').innerHTML = formatMoney(totalPrice);
    this.dataset.totalPrice = totalPrice;

    if (totalCompareAtPrice > totalPrice) {
      this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = formatMoney(totalCompareAtPrice);
      this.dataset.totalCompareAtPrice = totalCompareAtPrice;
    } else {
      this.price.querySelector('[data-ref="compare-at-price"]').innerHTML = '';
      this.dataset.totalCompareAtPrice = totalPrice;
    }
  }

  getPrices() {
    return {
      price: this.dataset.totalPrice,
      compareAtPrice: this.dataset.totalCompareAtPrice,
    };
  }
}
customElements.define('quantity-break-item', QuantityBreakItem);
